import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Download, 
  Calendar, 
  Loader2,
  Target,
  Lightbulb,
  Users,
  TrendingUp,
  PoundSterling,
  CheckCircle2,
  AlertTriangle,
  Lock,
  Sparkles,
  FileText,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import ReadinessBadge, { ReadinessBadgeInline } from "../components/ReadinessBadge";
import ReactMarkdown from "react-markdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessPlanPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders, loading: authLoading } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [isPro, setIsPro] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login?redirect=/business-plan/" + planId);
      return;
    }
    fetchPlan();
    fetchProStatus();
  }, [planId, user, authLoading]);

  const fetchPlan = async () => {
    try {
      const response = await axios.get(`${API}/business-plan/${planId}`, {
        headers: getAuthHeaders()
      });
      setPlan(response.data);
    } catch (error) {
      toast.error("Failed to load business plan");
      navigate("/hub");
    } finally {
      setLoading(false);
    }
  };

  const fetchProStatus = async () => {
    try {
      const response = await axios.get(`${API}/user/pro-status`, {
        headers: getAuthHeaders()
      });
      setIsPro(response.data.is_pro);
    } catch (error) {
      console.error("Failed to fetch pro status:", error);
    }
  };

  const handleDownloadPDF = async () => {
    if (!isPro) {
      toast.error("Pro subscription required for PDF download");
      return;
    }

    setDownloading(true);
    try {
      const response = await axios.get(
        `${API}/business-plan/${planId}/pdf`,
        {
          headers: getAuthHeaders(),
          responseType: "blob"
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `FoundrScore_Business_Plan_${planId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded!");
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Pro subscription required for PDF download");
      } else {
        toast.error("Failed to download PDF");
      }
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <p className="text-muted-foreground mb-4">Business plan not found</p>
        <Link to="/assessment">
          <Button>Take Assessment</Button>
        </Link>
      </div>
    );
  }

  const sections = plan.sections || {};
  const isAIGenerated = plan.ai_generated;

  return (
    <div className="py-8 md:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {isAIGenerated && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
                  <Sparkles className="w-3 h-3" />
                  AI-Tailored
                </span>
              )}
              <ReadinessBadgeInline level={plan.readiness_level} />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-1">
              {sections.business_name !== "To confirm" ? sections.business_name : "Your Business Plan"}
            </h1>
            <p className="text-muted-foreground">
              FoundrScore: {plan.foundrscore}/100
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isPro ? (
              <Button 
                onClick={handleDownloadPDF} 
                disabled={downloading}
                className="rounded-full"
                data-testid="download-pdf-btn"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download PDF
              </Button>
            ) : (
              <Link to="/pricing">
                <Button variant="outline" className="rounded-full" data-testid="upgrade-for-pdf-btn">
                  <Lock className="w-4 h-4 mr-2" />
                  Upgrade for PDF
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* AI-Generated Content */}
        {isAIGenerated && plan.ai_content ? (
          <Card className="mb-8" data-testid="ai-plan-content">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Your Tailored Business Plan
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <ReactMarkdown
                components={{
                  h1: ({children}) => <h2 className="text-xl font-bold mt-6 mb-3 text-primary">{children}</h2>,
                  h2: ({children}) => <h3 className="text-lg font-semibold mt-5 mb-2">{children}</h3>,
                  h3: ({children}) => <h4 className="text-base font-medium mt-4 mb-2">{children}</h4>,
                  p: ({children}) => <p className="mb-3 text-muted-foreground leading-relaxed">{children}</p>,
                  ul: ({children}) => <ul className="list-disc list-inside mb-4 space-y-1">{children}</ul>,
                  ol: ({children}) => <ol className="list-decimal list-inside mb-4 space-y-1">{children}</ol>,
                  li: ({children}) => <li className="text-muted-foreground">{children}</li>,
                  strong: ({children}) => <strong className="font-semibold text-foreground">{children}</strong>,
                }}
              >
                {plan.ai_content}
              </ReactMarkdown>
            </CardContent>
          </Card>
        ) : (
          /* Rule-based plan display */
          <div className="space-y-6">
            {/* One-Line Pitch */}
            {sections.one_line_pitch && sections.one_line_pitch !== "To confirm" && (
              <Card>
                <CardContent className="p-6">
                  <p className="text-lg italic text-center">"{sections.one_line_pitch}"</p>
                </CardContent>
              </Card>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              {/* Problem */}
              <Card data-testid="section-problem">
                <CardContent className="p-6">
                  <h3 className="flex items-center gap-2 font-semibold text-primary mb-4">
                    <Target className="w-5 h-5" />
                    The Problem
                  </h3>
                  <p className="text-muted-foreground">{sections.problem || "To confirm"}</p>
                </CardContent>
              </Card>

              {/* Target Customer */}
              <Card data-testid="section-customer">
                <CardContent className="p-6">
                  <h3 className="flex items-center gap-2 font-semibold text-primary mb-4">
                    <Users className="w-5 h-5" />
                    Target Customer
                  </h3>
                  <p className="text-muted-foreground">{sections.target_customer || "To confirm"}</p>
                </CardContent>
              </Card>

              {/* Solution */}
              <Card data-testid="section-solution">
                <CardContent className="p-6">
                  <h3 className="flex items-center gap-2 font-semibold text-primary mb-4">
                    <Lightbulb className="w-5 h-5" />
                    Solution
                  </h3>
                  <p className="text-muted-foreground">{sections.solution || "To confirm"}</p>
                </CardContent>
              </Card>

              {/* Market & Competition */}
              <Card data-testid="section-market">
                <CardContent className="p-6">
                  <h3 className="flex items-center gap-2 font-semibold text-primary mb-4">
                    <TrendingUp className="w-5 h-5" />
                    Market & Competition
                  </h3>
                  <p className="text-muted-foreground whitespace-pre-line">{sections.market_competition || "To confirm"}</p>
                </CardContent>
              </Card>

              {/* Revenue & Pricing */}
              <Card data-testid="section-revenue">
                <CardContent className="p-6">
                  <h3 className="flex items-center gap-2 font-semibold text-primary mb-4">
                    <PoundSterling className="w-5 h-5" />
                    Revenue & Pricing
                  </h3>
                  <p className="text-muted-foreground">{sections.revenue_pricing || "To confirm"}</p>
                </CardContent>
              </Card>

              {/* Go-to-Market */}
              <Card data-testid="section-gtm">
                <CardContent className="p-6">
                  <h3 className="flex items-center gap-2 font-semibold text-primary mb-4">
                    <Target className="w-5 h-5" />
                    Go-to-Market
                  </h3>
                  <p className="text-muted-foreground">{sections.go_to_market || "To confirm"}</p>
                </CardContent>
              </Card>

              {/* Costs & Funding */}
              <Card data-testid="section-costs">
                <CardContent className="p-6">
                  <h3 className="flex items-center gap-2 font-semibold text-primary mb-4">
                    <PoundSterling className="w-5 h-5" />
                    Costs & Funding
                  </h3>
                  <p className="text-muted-foreground">{sections.costs_funding || "To confirm"}</p>
                </CardContent>
              </Card>

              {/* Team */}
              <Card data-testid="section-team">
                <CardContent className="p-6">
                  <h3 className="flex items-center gap-2 font-semibold text-primary mb-4">
                    <Users className="w-5 h-5" />
                    Founder & Team
                  </h3>
                  <p className="text-muted-foreground">{sections.team || "To confirm"}</p>
                </CardContent>
              </Card>
            </div>

            {/* Risks & Actions */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Top Risks */}
              {sections.top_risks && sections.top_risks.length > 0 && (
                <Card className="border-red-200 bg-red-50/50" data-testid="section-risks">
                  <CardContent className="p-6">
                    <h3 className="flex items-center gap-2 font-semibold text-red-700 mb-4">
                      <AlertTriangle className="w-5 h-5" />
                      Top 3 Risks
                    </h3>
                    <ol className="space-y-2">
                      {sections.top_risks.map((risk, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-red-800">
                          <span className="font-bold">{i + 1}.</span>
                          <span>{risk}</span>
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              )}

              {/* Next Actions */}
              {sections.next_actions && sections.next_actions.length > 0 && (
                <Card className="border-emerald-200 bg-emerald-50/50" data-testid="section-actions">
                  <CardContent className="p-6">
                    <h3 className="flex items-center gap-2 font-semibold text-emerald-700 mb-4">
                      <CheckCircle2 className="w-5 h-5" />
                      Next 3 Actions
                    </h3>
                    <ol className="space-y-2">
                      {sections.next_actions.map((action, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-emerald-800">
                          <span className="font-bold">{i + 1}.</span>
                          <span>{action}</span>
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Pro Upgrade CTA for non-Pro users */}
        {!isPro && (
          <Card className="mt-8 border-primary/20 bg-primary/5" data-testid="pro-upgrade-cta">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Upgrade to Pro</h3>
                    <p className="text-sm text-muted-foreground">
                      Get an AI-tailored business plan, PDF download, 30-day action plan, and more
                    </p>
                  </div>
                </div>
                <Link to="/pricing">
                  <Button className="rounded-full">
                    View Pricing
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bottom Navigation */}
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/hub">
            <Button variant="outline" className="rounded-full">
              <FileText className="w-4 h-4 mr-2" />
              Validation Hub
            </Button>
          </Link>
          <a href="https://calendly.com" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="rounded-full">
              <Calendar className="w-4 h-4 mr-2" />
              Book Advisory Call
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
